import { LearningLayout } from "@jambushrushti/components/learning/LearningLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Textarea } from "@jambushrushti/components/ui/textarea";
import { Switch } from "@jambushrushti/components/ui/switch";
import { Label } from "@jambushrushti/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@jambushrushti/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@jambushrushti/components/ui/table";
import {
  Plus,
  Pencil,
  Trash2,
  Layers,
  Search,
  DollarSign,
  BookOpen,
} from "lucide-react";
import { useState } from "react";
import {
  usePrograms,
  useSaveProgram,
  useDeleteProgram,
} from "@jambushrushti/hooks/useLMSPrograms";

const levels = ["Beginner", "Intermediate", "Advanced", "Expert"];

const ProgramsManage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    duration: "",
    level: "Beginner",
    is_published: false,
    is_paid: false,
    price: 0,
    thumbnail_url: "",
  });

  const { data: programs = [], isLoading } = usePrograms();
  const saveProgram = useSaveProgram();
  const deleteProgram = useDeleteProgram();

  const filteredPrograms = programs.filter(p =>
    p.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      duration: "",
      level: "Beginner",
      is_published: false,
      is_paid: false,
      price: 0,
      thumbnail_url: "",
    });
    setEditingProgram(null);
  };

  const handleEdit = (program: any) => {
    setEditingProgram(program);
    setFormData({
      title: program.title,
      description: program.description || "",
      duration: program.duration || "",
      level: program.level || "Beginner",
      is_published: program.is_published,
      is_paid: program.is_paid,
      price: program.price || 0,
      thumbnail_url: program.thumbnail_url || "",
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    await saveProgram.mutateAsync({
      ...(editingProgram ? { id: editingProgram.id } : {}),
      ...formData,
    });
    setIsDialogOpen(false);
    resetForm();
  };

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this program?")) {
      await deleteProgram.mutateAsync(id);
    }
  };

  return (
    <LearningLayout title="Programs">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h1 className="font-heading text-2xl font-bold">Manage Programs</h1>
            <p className="text-muted-foreground">
              Create learning paths by bundling courses
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Program
          </Button>
        </div>

        {/* Stats */}
        <div className="grid gap-4 sm:grid-cols-4">
          <Card className="border-border">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-lg bg-primary/10 p-2 text-primary">
                <Layers className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{programs.length}</p>
                <p className="text-xs text-muted-foreground">Total Programs</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-lg bg-green-500/10 p-2 text-green-600">
                <BookOpen className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {programs.filter(p => p.is_published).length}
                </p>
                <p className="text-xs text-muted-foreground">Published</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-lg bg-blue-500/10 p-2 text-blue-600">
                <DollarSign className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {programs.filter(p => p.is_paid).length}
                </p>
                <p className="text-xs text-muted-foreground">Paid Programs</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="bg-orange/10 text-orange rounded-lg p-2">
                <Layers className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {programs.filter(p => !p.is_paid).length}
                </p>
                <p className="text-xs text-muted-foreground">Free Programs</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search programs..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Table */}
        <Card className="border-border">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Program</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-8 text-center text-muted-foreground"
                    >
                      Loading programs...
                    </TableCell>
                  </TableRow>
                ) : filteredPrograms.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-8 text-center text-muted-foreground"
                    >
                      No programs found. Create your first program!
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPrograms.map(program => (
                    <TableRow key={program.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{program.title}</p>
                          <p className="line-clamp-1 text-sm text-muted-foreground">
                            {program.description}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>{program.level}</TableCell>
                      <TableCell>{program.duration || "-"}</TableCell>
                      <TableCell>
                        {program.is_paid ? `₹${program.price}` : "Free"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            program.is_published ? "default" : "secondary"
                          }
                        >
                          {program.is_published ? "Published" : "Draft"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(program)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDelete(program.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingProgram ? "Edit Program" : "Add New Program"}
              </DialogTitle>
              <DialogDescription>
                Create a learning path by bundling multiple courses together.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={e =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="Enter program title"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={e =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Describe the program"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Level</Label>
                  <Select
                    value={formData.level}
                    onValueChange={value =>
                      setFormData({ ...formData, level: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {levels.map(level => (
                        <SelectItem key={level} value={level}>
                          {level}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Duration</Label>
                  <Input
                    value={formData.duration}
                    onChange={e =>
                      setFormData({ ...formData, duration: e.target.value })
                    }
                    placeholder="e.g., 12 weeks"
                  />
                </div>
              </div>

              <div>
                <Label>Thumbnail URL</Label>
                <Input
                  value={formData.thumbnail_url}
                  onChange={e =>
                    setFormData({ ...formData, thumbnail_url: e.target.value })
                  }
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Paid Program</Label>
                <Switch
                  checked={formData.is_paid}
                  onCheckedChange={checked =>
                    setFormData({ ...formData, is_paid: checked })
                  }
                />
              </div>

              {formData.is_paid && (
                <div>
                  <Label>Price (₹)</Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        price: parseFloat(e.target.value) || 0,
                      })
                    }
                    placeholder="0"
                  />
                </div>
              )}

              <div className="flex items-center justify-between">
                <Label>Publish Program</Label>
                <Switch
                  checked={formData.is_published}
                  onCheckedChange={checked =>
                    setFormData({ ...formData, is_published: checked })
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!formData.title || saveProgram.isPending}
              >
                {saveProgram.isPending
                  ? "Saving..."
                  : editingProgram
                    ? "Update"
                    : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </LearningLayout>
  );
};

export default ProgramsManage;
